import { useQueries, useQuery } from "react-query";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { IApiProperty, IRawProperty, TApiProperty, TRawProperty } from "@propely/propely-typings";
import { propelyApi } from "@src/libs/firebase/api";
import { z } from "zod";
import { useCompaniesByIDs, useCompanyByID } from "../company/company.service";
import { useMemo } from "react";

// Method to get a property fetch key
export const usePropertyKey = (propertyID: string | undefined) => ["properties", { propertyID }];
export const allPropertiesKey = ["properties", {}];

// Method to fetch a property
const fetchProperty = async (propertyID: string | undefined): Promise<IRawProperty> => {
  if (!propertyID) {
    throw new Error("Property ID is required");
  }
  const property = await getDoc(doc(getFirestore(), "properties", propertyID));
  return TRawProperty.parse({
    id: property.id,
    ...property.data(),
  });
};

// Hook to get a single property
export const useProperty = (propertyID: string | undefined) => {
  const $property = useQuery(usePropertyKey(propertyID), () => fetchProperty(propertyID), {
    enabled: !!propertyID,
  });
  return {
    $property,
    property: $property.data,
  };
};

// Hook to get multiple properties
export const useProperties = (propertyIDs: string[], { enabled }: { enabled?: boolean } = { enabled: true }) => {
  const $properties = useQueries(
    propertyIDs.map((propertyID) => ({
      queryKey: usePropertyKey(propertyID),
      queryFn: () => fetchProperty(propertyID),
      enabled,
    }))
  );

  const properties = $properties.map(($property) => $property.data).filter((data): data is IRawProperty => Boolean(data));
  const isLoading = $properties.some((result) => result.isLoading);

  return {
    $properties,
    properties: properties,
    isLoading,
  };
};

export const useAllPropertiesKey = () => "properties";

export const useAllProperties = ({ enabled }: { enabled?: boolean } = {}) => {
  return useQuery(
    useAllPropertiesKey(),
    async (): Promise<IApiProperty[]> => {
      const response = await propelyApi().get("/properties");
      return z.array(TApiProperty).parse(response.data.datas);
    },
    {
      enabled,
    }
  );
};

export type UpdatePropertyInput = {
  propertyId: string;
  data: { [key: string]: { value: any; suffix?: string } };
};
export const updatePropertyInfoFields = (input: UpdatePropertyInput) => {
  const firebaseFunctions = getFunctions(undefined, "europe-west1");
  const updatePropertyInfoFields = httpsCallable(firebaseFunctions, "updatePropertyInfoFields");

  return updatePropertyInfoFields({
    propertyID: input.propertyId,
    fields: input.data,
  });
};

/**
 * Hook to get all properties in a company, including properties in administrated companies.
 */
export const useAllPropertiesInCompany = (companyID: string) => {
  const { company } = useCompanyByID(companyID);
  const { companies } = useCompaniesByIDs(company?.administrating || []);
  const propertyIDs: string[] = useMemo(() => {
    const result: Set<string> = new Set();
    if (company?.properties) company.properties.forEach((propertyID) => result.add(propertyID));
    for (const company of companies) {
      if (company?.properties) company.properties.forEach((propertyID) => result.add(propertyID));
    }
    return Array.from(result);
  }, [company, companies]);

  const { properties, $properties } = useProperties(propertyIDs);

  return {
    $properties,
    properties: properties.filter((p) => !p.deletedBy),
  };
};
